import {useEffect, useRef} from 'react';
import {useLocation, useSearchParams} from 'react-router';
import {useAuth} from '@common/auth/use-auth';
import {apiClient, queryClient} from '@common/http/query-client';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {REFERRAL_OFFER_KEY, REFERRAL_WELCOME_EVENT} from '@app/referrals/referral-offer';
import {isViewingStudent} from '@app/staff/student-view';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setInLocalStorage,
} from '@ui/utils/hooks/local-storage';

/**
 * Tell the server that somebody arrived through a partner link, and attach the
 * claim once they have an account.
 *
 * The gap this closes: a link is clicked by a STRANGER and a commission
 * belongs to an ACCOUNT, and between the two sits a sign-up that can happen
 * days later, on a different page.
 *
 * The server now owns attribution — it mints the visitor token, records the
 * landing page, applies the first/last-click rule and enforces the expiry
 * window. This hook's whole job is to notice the `ref` parameter and hand it
 * over. It deliberately does NOT decide anything: a browser that chose its own
 * visitor token could be used by one partner to claim anybody.
 *
 * localStorage is kept as a fallback only. The real carrier is the `mas_ref`
 * cookie the server sets, which survives a visitor clearing a tab, works
 * across subdomains, and expires on the schedule an admin configured.
 */
const KEY = 'partner-ref';

/**
 * Set for THIS tab when a ref arrives in the URL, and read on every
 * navigation to decide whether to keep it there.
 *
 * sessionStorage, not localStorage, on purpose. It has to die when the tab
 * does: if a visitor opens the site fresh tomorrow with a clean URL, we must
 * not start decorating their address bar with a code they did not bring.
 */
const STICKY_KEY = 'partner-ref-sticky';

/** Codes are short and alphanumeric. Anything else is not one of ours. */
const LOOKS_LIKE_CODE = /^[A-Za-z0-9._-]{3,64}$/;

export function useReferralCapture() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const {isLoggedIn} = useAuth();
  const fromUrl = params.get('ref');

  // One claim attempt per session. Without this, every navigation would post
  // again — the server is idempotent, but a request per page view is waste.
  const claimed = useRef(false);
  // The code from the URL, kept after `?ref=` is removed from the address.
  const capturedCode = useRef<string | null>(null);
  if (fromUrl && LOOKS_LIKE_CODE.test(fromUrl)) capturedCode.current = fromUrl;

  /**
   * Remove `?ref=` from the address bar once it has been read.
   *
   * Attribution lives in the `mas_ref` cookie the server sets, so the code is
   * only needed on the first page. Leaving it in the URL made every reload
   * count as a new click and put partner codes into bookmarks and shared
   * links. The track call below captures the code before this runs (it reads
   * `fromUrl` from the same render).
   */
  useEffect(() => {
    if (!fromUrl) return;
    try {
      sessionStorage.removeItem(STICKY_KEY);
    } catch {
      /* ignore */
    }
    setParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('ref');
        return next;
      },
      {replace: true, preventScrollReset: true},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  // Record the arrival the moment it happens, signed in or not, and tell the
  // server WHERE they landed. The landing page is the whole reason a partner
  // can tell a scholarship link from a homepage link.
  useEffect(() => {
    if (!fromUrl || !LOOKS_LIKE_CODE.test(fromUrl)) return;
    // Attribution describes the person browsing. A staff member reading a
    // student's portal is not a visitor arriving from a partner link, and the
    // server refuses writes while that view is open — so this would be a
    // guaranteed 403 in the console on every page they open.
    if (isViewingStudent()) return;

    try {
      setInLocalStorage(KEY, fromUrl);
    } catch {
      /* private window, blocked storage — the cookie still carries it */
    }

    apiClient
      .post('referral/track', {
        code: fromUrl,
        landing_path: location.pathname + location.search,
      })
      .then(r => {
        if (r.data?.own_link) {
          // Once per tab: a partner testing their own link.
          try {
            if (!sessionStorage.getItem('partner-own-link-notice')) {
              sessionStorage.setItem('partner-own-link-notice', '1');
              toast(message('This is your own partner link. The discount and commission apply to students who open it — to see what they see, open it in a private window.'), {duration: 10000});
            }
          } catch {
            /* ignore */
          }
          return;
        }
        const offer = r.data?.offer ?? null;
        // The cookie now exists: prices and the cart can show the discount.
        queryClient.setQueryData(REFERRAL_OFFER_KEY, {offer});
        queryClient.invalidateQueries({queryKey: ['cart']});
        if (offer) {
          window.dispatchEvent(new CustomEvent(REFERRAL_WELCOME_EVENT, {detail: offer}));
        }
      })
      .catch(() => {
        /* attribution is never worth interrupting a page load for */
      });
    // location.search is captured deliberately at the moment of arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  // Attach the claim once there is an account to attach it to. The server
  // reads its own cookie; the stored code is passed only as a fallback for a
  // browser that dropped it.
  useEffect(() => {
    if (!isLoggedIn || claimed.current || isViewingStudent()) return;
    claimed.current = true;

    let stored: string | null = null;
    try {
      stored = getFromLocalStorage<string>(KEY) ?? null;
    } catch {
      /* ignore */
    }

    const code = fromUrl ?? stored ?? capturedCode.current;

    apiClient
      .post('referral/claim', code && LOOKS_LIKE_CODE.test(code) ? {code} : {})
      .then(() => {
        // Now attached to an account: the offer (first-order rule) and the
        // cart are priced for the account.
        queryClient.invalidateQueries({queryKey: REFERRAL_OFFER_KEY});
        queryClient.invalidateQueries({queryKey: ['cart']});
        // Cleared either way: the server refuses a code it does not like, and
        // retrying it on every load would be a request that can never succeed.
        try {
          removeFromLocalStorage(KEY);
        } catch {
          /* ignore */
        }
      })
      .catch(() => {});
  }, [isLoggedIn, fromUrl]);
}
